import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Star, Sparkles } from "lucide-react";
import PaymentMethodsBadge from "@/components/listings/detail/PaymentMethodsBadge";
import type { SellPackagesCopy } from "./sellPackagesCopy";

export type SellPkg = "standard" | "promoted" | "until_sold";

/** The three paid private-listing packages, shared by /vinde-masina and /vinde-masina/{marca}. */
export default function SellPackagesGrid({
  t,
  highlight,
  onChoose,
}: {
  t: SellPackagesCopy;
  highlight: boolean;
  onChoose: (pkg: SellPkg) => void;
}) {
  return (
    <>
      <div id="sell-packages" className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-10 scroll-mt-24">
        {/* STANDARD */}
        <Card className="relative border-border">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">{t.standardTitle}</h2>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-3xl font-bold text-foreground">{t.standardPrice}</span>
            </div>
            <p className="text-xs text-muted-foreground mb-5 min-h-[2.5rem]">{t.standardSub}</p>
            <ul className="space-y-2 text-sm text-foreground/80">
              <li className="flex gap-2"><CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />{t.standardF1}</li>
              <li className="flex gap-2"><CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />{t.standardF2}</li>
              <li className="flex gap-2"><CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />{t.standardF3}</li>
              <li className="flex gap-2"><Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />{t.aiIncluded}</li>
            </ul>
            <Button onClick={() => onChoose("standard")} variant="outline" size="lg" className="w-full mt-6">
              {t.chooseStandard}
            </Button>
          </CardContent>
        </Card>

        {/* PROMOTED — recommended */}
        <Card className={`relative border-2 border-primary shadow-lg transition-shadow ${highlight ? "ring-4 ring-primary/40" : ""}`}>
          <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground gap-1">
            <Star className="h-3.5 w-3.5 fill-current" /> {t.recommended}
          </Badge>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">{t.promotedTitle}</h2>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-3xl font-bold text-foreground">{t.promotedPrice}</span>
            </div>
            <p className="text-xs text-muted-foreground mb-5 min-h-[2.5rem]">{t.promotedSub}</p>
            <ul className="space-y-2 text-sm text-foreground/80">
              <li className="flex gap-2"><CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />{t.promotedF1}</li>
              <li className="flex gap-2"><CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />{t.promotedF2}</li>
              <li className="flex gap-2"><CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />{t.promotedF3}</li>
              <li className="flex gap-2"><Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />{t.aiIncluded}</li>
            </ul>
            <Button onClick={() => onChoose("promoted")} size="lg" className="w-full mt-6">
              {t.choosePromoted}
            </Button>
          </CardContent>
        </Card>

        {/* ACTIVE UNTIL SOLD */}
        <Card className="relative border-border">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">{t.activeTitle}</h2>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-3xl font-bold text-foreground">{t.activePrice}</span>
              <span className="text-sm text-muted-foreground">{t.perMonth}</span>
            </div>
            <p className="text-xs text-muted-foreground mb-5 min-h-[2.5rem]">{t.activeSub}</p>
            <ul className="space-y-2 text-sm text-foreground/80">
              <li className="flex gap-2"><CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />{t.activeF1}</li>
              <li className="flex gap-2"><CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />{t.activeF2}</li>
              <li className="flex gap-2"><CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />{t.activeF3}</li>
              <li className="flex gap-2"><Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />{t.aiIncluded}</li>
            </ul>
            <Button onClick={() => onChoose("until_sold")} variant="outline" size="lg" className="w-full mt-6">
              {t.chooseActive}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="text-center">
        <p className="text-xs md:text-sm text-muted-foreground max-w-2xl mx-auto">{t.footer}</p>
        <div className="mt-6 flex justify-center">
          <PaymentMethodsBadge />
        </div>
      </div>
    </>
  );
}
